import styled, { css } from 'styled-components';

import { rem, mediaUp } from 'approot/shared/style';
import {
  grey1,
  grey2,
  grey4,
  grey5,
  grey8,
  grey9,
  grey10,
  purple1,
  purple7,
  purple8,
  purple9,
  grey3,
  grey6,
} from 'approot/shared/styles/colours.style';
import {
  spacing4,
  spacing8,
  spacing12,
  spacing16,
  spacing24,
} from 'approot/shared/styles/spacing.style';
import {
  fontFamilyBase,
  fontSize16,
  fontSize14,
  fontSize18,
  fontSize16LineHeight,
} from 'approot/shared/styles/typography.style';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import {
  standardFocusState,
  standardInsetBoxShadow,
} from 'approot/shared/styles/borders.style';

type PillsInputsProps = {
  $centered?: boolean;
};

export const PillsInputs = styled.div<PillsInputsProps>`
  display: flex;
  flex-wrap: wrap;
  text-align: center;
  margin-bottom: ${rem(spacing8)};

  ${props => props.$centered && `justify-content: center;`}
`;

type PillLabelProps = {
  $hasBorder?: boolean;
  $isIconPicker?: boolean;
  $centered?: boolean;
  $hasIcon?: boolean;
  $isSmall?: boolean;
};

export const PillLabelShared = css<PillLabelProps>`
  cursor: pointer;
  border: none;
  border-radius: ${rem(54)};
  background-color: ${grey1};
  min-width: ${rem(43)};
  padding: ${rem(spacing8)} ${rem(spacing16)};
  font-size: ${rem(fontSize14)};
  font-family: ${fontFamilyBase};
  line-height: 1.6;
  stroke: currentcolor;
  fill: transparent;

  ${props =>
    props.$centered
      ? `margin: 0 ${rem(spacing8)} ${rem(spacing16)};`
      : `margin: 0 ${rem(spacing12)} ${rem(spacing16)} 0;`}

  ${props => props.$hasBorder && `border: 1px solid ${grey5};`}

  ${props =>
    props.$isIconPicker &&
    `
      padding: .4rem;
      border-radius: 100%;
  `}

  ${props =>
    props.$hasIcon &&
    `
      display: flex;
      align-items: center;
  `}

  ${props =>
    props.$isSmall
      ? `
    height: ${rem(32)};
    min-width: ${rem(32)};
    margin-right: ${rem(spacing8)};
    padding: ${rem(spacing4)} ${rem(spacing8)};
  `
      : `
    ${mediaUp.md<PillLabelProps>`
      font-size: ${rem(fontSize16)};
      min-width: ${rem(43)};

      ${props =>
        props.$hasIcon &&
        `
        padding-left: ${rem(spacing16)};
      `}
    `}
  `}
`;

export const PillIcon = styled(InlineSvg)`
  width: ${rem(22)};
  height: ${rem(22)};
  margin-right: ${rem(spacing12)};
  flex-shrink: 0;

  fill: ${grey8};
  stroke: none;
`;

export const PillLabel = styled.label<PillLabelProps>`
  ${PillLabelShared}
  background-color: ${grey1};
  color: ${grey9};

  &:hover {
    background-color: ${grey2};
    border-color: ${grey9};
    color: ${grey10};
  }
`;

export const PillInputStyled = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  border: 0;
  padding: 0;
  white-space: nowrap;
  clip-path: inset(100%);
  clip: rect(0 0 0 0);
  overflow: hidden;

  &:focus + label,
  &:active + label {
    background-color: ${grey2};
    border-color: ${grey9};
    box-shadow: 0 0 0 1px ${grey9};
    color: ${grey10};
  }

  &:active + label {
    ${standardInsetBoxShadow}
  }

  &:checked + label {
    background-color: ${purple1};
    border-color: ${purple7};
    color: ${purple8};

    ${PillIcon} {
      fill: currentColor;
    }
  }

  &:checked + label:hover {
    background-color: ${purple1};
    border-color: ${purple9};
    color: ${purple9};
  }

  &:checked + label:focus {
    background-color: ${purple1};
    border-color: ${grey9};
    box-shadow: 0 0 0 1px ${grey9};
    color: ${purple8};
  }

  &:disabled + label {
    background-color: ${grey3};
    border-color: ${grey5};
    color: ${grey6};
    cursor: not-allowed;
    box-shadow: none !important;
  }
`;

type PillProps = {
  $active?: boolean;
  $noBorder?: boolean;
};

export const Pill = styled.button<PillProps>`
  display: inline;
  margin: 0 ${rem(spacing8)} ${rem(spacing16)};
  border: ${props =>
    props.$noBorder ? `1px solid ${grey1}` : `1px solid ${grey8}`};
  border-radius: ${rem(25)};
  background-color: ${grey1};
  padding: ${rem(9)} ${rem(spacing16)};
  font-size: ${rem(fontSize16)};
  line-height: 1;
  stroke: currentColor;
  fill: transparent;
  color: ${grey8};

  ${mediaUp.lg`
    font-size: ${rem(fontSize18)};
    padding: ${rem(11)} ${rem(spacing24)};
  `}

  &:hover {
    background-color: ${grey4};
    border: ${props =>
      props.$noBorder ? `1px solid ${grey4}` : `1px solid ${grey8}`};
  }

  ${standardFocusState}

  ${props =>
    props.$active &&
    `
    color: ${grey1};
    background-color: ${purple7};
    border: 1px solid ${purple7};

    &:hover {
      color: ${grey1};
      background-color: ${purple7};
      border: 1px solid ${purple7};
    }
  `}
`;

export const PillButtonBaseStyled = css`
  height: ${rem(40)};
  padding: ${rem(spacing8)} ${rem(spacing16)};
  border-radius: 85px;
  background-color: ${grey1};
  border: 1px solid ${grey5};
  color: ${grey9};
  font-size: ${rem(fontSize16)};
  line-height: ${rem(fontSize16LineHeight)};
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

type PillRemovableStyledProps = {
  $isRemovable?: boolean;
};

export const PillRemovableStyled = styled.div<PillRemovableStyledProps>`
  max-width: 100%;
  ${PillButtonBaseStyled};
  padding: ${rem(spacing8)} ${rem(spacing16)};
  ${({ $isRemovable }) =>
    $isRemovable ? `padding-right: ${rem(spacing8)}` : ''};
  justify-content: space-between;
`;

export const PillRemovableTextStyled = styled.span<PillRemovableStyledProps>`
  max-width: ${({ $isRemovable }) => ($isRemovable ? '90%' : '100%')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PillButtonContainerStyled = styled.div`
  margin-left: ${rem(spacing8)};
  > button {
    height: auto;
    padding: ${rem(spacing8)};
  }
`;
